import { QueryHookOptions } from '@apollo/client';
import { useMemo } from 'react';

import { PaginationInfo, usePaginatedQuery } from '@occc-app/hooks/usePaginatedQuery';
import {
  RetailerNetworkTobaccoProgramMetricType,
  RetailerNetworkTobaccoProgramSitesMetricFilter,
  RetailerNetworkTobaccoProgramSitesMetricQuery,
  RetailerNetworkTobaccoProgramSitesMetricQueryVariables,
  useRetailerNetworkTobaccoProgramSitesMetricQuery,
} from '@occc/graphql';

import {
  LOCATION_RANKING_TABLE_DEFAULT_PAGE_SIZE,
  LOCATION_RANKING_TABLE_DEFAULT_SORT_DIRECTION,
  LOCATION_RANKING_TABLE_DEFAULT_SORT_FIELD,
} from '../retailer-network-tobacco-performance-details-page.constants';

type RetailerNetworkTobaccoProgramSitesMetricOptions = {
  metricType: RetailerNetworkTobaccoProgramMetricType;
  filter: RetailerNetworkTobaccoProgramSitesMetricFilter;
  baseOptions?: QueryHookOptions<
    RetailerNetworkTobaccoProgramSitesMetricQuery,
    RetailerNetworkTobaccoProgramSitesMetricQueryVariables
  >;
  paginationInfo?: PaginationInfo;
  onPageChange?: (info: PaginationInfo) => void;
};

export function useRetailerNetworkTobaccoProgramSitesMetric(
  options: RetailerNetworkTobaccoProgramSitesMetricOptions,
) {
  const { metricType, filter, baseOptions, paginationInfo, onPageChange } = options;
  const { data: queryData, ...query } = usePaginatedQuery(
    useRetailerNetworkTobaccoProgramSitesMetricQuery,
    'retailerNetworkTobaccoProgramSitesMetric.pageInfo',
    LOCATION_RANKING_TABLE_DEFAULT_PAGE_SIZE,
    {
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
      variables: {
        metricType,
        filter,
        sort: {
          field: LOCATION_RANKING_TABLE_DEFAULT_SORT_FIELD,
          order: LOCATION_RANKING_TABLE_DEFAULT_SORT_DIRECTION,
        },
      },
      ...baseOptions,
    },
    paginationInfo,
    onPageChange,
  );

  const data = useMemo(() => {
    if (!queryData) {
      return queryData;
    }

    if (!Array.isArray(queryData.retailerNetworkTobaccoProgramSitesMetric?.edges)) {
      return [];
    }

    return queryData.retailerNetworkTobaccoProgramSitesMetric.edges.map((edge) => edge.node);
  }, [queryData]);

  return {
    data,
    ...query,
  };
}
